
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { debugWarn, debugLog } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';
import { waitForHydrationComplete } from '@/utils/hydrationManager';

type Theme = "dark" | "light" | "system";
type MetalTheme = "gold" | "silver" | null;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultMetal?: MetalTheme;
  storageKey?: string;
  metalStorageKey?: string;
  ssrMode?: boolean;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  metalTheme: MetalTheme;
  setMetalTheme: (metal: MetalTheme) => void;
  toggleMetalTheme: () => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  metalTheme: null,
  setMetalTheme: () => null,
  toggleMetalTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultMetal = "gold",
  storageKey = "gildy-ui-theme",
  metalStorageKey = "gildy-ui-metal",
  ssrMode = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [metalTheme, setMetalThemeState] = useState<MetalTheme>(defaultMetal);
  const [mounted, setMounted] = useState<boolean>(false);

  // 🛡️ FIX: Wait for hydration complete before setting mounted and reading localStorage
  // This prevents React #421 by deferring all state updates until hydration is settled
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    let cancelled = false;
    
    const initTheme = async () => {
      // 🛡️ If SSR mode, wait for hydration to complete before any state updates
      if (ssrMode) {
        await waitForHydrationComplete();
        
        if (cancelled) return;
        
        // 🛡️ Additional tick for Suspense settling
        await new Promise(resolve => setTimeout(resolve, 0));
        
        if (cancelled) return;
      }
      
      debugLog('general', 'ThemeProvider: setting mounted=true (post-hydration)', undefined, 'ThemeProvider');
      setMounted(true);
      
      // Only attempt to load from localStorage when we're certain we're client-side
      // Uses safeLocalStorage for GDPR compliance
      try {
        // Load theme from localStorage if available
        const savedTheme = safeLocalStorage.getItem(storageKey) as Theme | null;
        if (savedTheme) {
          setThemeState(savedTheme);
        }
        
        // Load metal theme from localStorage if available
        const savedMetal = safeLocalStorage.getItem(metalStorageKey) as MetalTheme | null;
        if (savedMetal) {
          setMetalThemeState(savedMetal);
        }
      } catch (e) {
        // Ignore localStorage errors (e.g., in incognito mode)
        debugWarn('general', "Could not access localStorage:", e, 'ThemeProvider');
      }
    };
    
    initTheme();
    
    return () => {
      cancelled = true;
    };
  }, [storageKey, metalStorageKey, ssrMode]);

  // Update theme class on document when theme changes
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Wait until component is mounted
    if (!mounted) return;
    
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
    
    // Add transition class for smooth theme changes
    root.style.transition = "background-color 0.5s ease, color 0.5s ease";
  }, [theme, mounted]);

  // Update metal theme class when it changes
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Wait until component is mounted
    if (!mounted) return;
    
    const root = window.document.documentElement;
    root.classList.remove("gold-theme", "silver-theme");
    
    if (metalTheme) {
      root.classList.add(`${metalTheme}-theme`);
    }
  }, [metalTheme, mounted]);

  // HYDRATION FIX: Use useMemo to compute context value
  // This ensures same Provider tree structure for SSR and client
  // Only the VALUE changes after mount, not the tree structure
  const contextValue = useMemo<ThemeProviderState>(() => {
    // No-op functions for SSR/pre-mount (safe, deterministic)
    const noopSetTheme = () => null;
    const noopSetMetal = () => null;
    const noopToggle = () => null;

    // Before mount: return static values matching SSR output
    if (!mounted) {
      return {
        theme: defaultTheme,
        setTheme: noopSetTheme,
        metalTheme: defaultMetal,
        setMetalTheme: noopSetMetal,
        toggleMetalTheme: noopToggle
      };
    }

    // After mount: return dynamic values with working setters
    return {
      theme,
      setTheme: (newTheme: Theme) => {
        try {
          if (typeof window !== 'undefined') {
            safeLocalStorage.setItem(storageKey, newTheme);
          }
        } catch (e) {
          debugWarn('general', "Could not store theme preference:", e, 'ThemeProvider');
        }
        setThemeState(newTheme);
      },
      metalTheme,
      setMetalTheme: (metal: MetalTheme) => {
        try {
          if (typeof window !== 'undefined') {
            safeLocalStorage.setItem(metalStorageKey, metal || "");
          }
        } catch (e) {
          debugWarn('general', "Could not store metal theme preference:", e, 'ThemeProvider');
        }
        setMetalThemeState(metal);
      },
      toggleMetalTheme: () => {
        if (typeof window === 'undefined' || !mounted) return;
        
        try {
          const newMetal = metalTheme === "gold" ? "silver" : "gold";
          safeLocalStorage.setItem(metalStorageKey, newMetal);
          setMetalThemeState(newMetal);
        } catch (e) {
          debugWarn('general', "Could not update localStorage:", e, 'ThemeProvider');
          setMetalThemeState(metalTheme === "gold" ? "silver" : "gold");
        }
      }
    };
  }, [mounted, theme, metalTheme, defaultTheme, defaultMetal, storageKey, metalStorageKey]);

  // HYDRATION FIX: Single return path - same tree structure always
  // Only the context VALUE differs between SSR and client, not the tree
  return (
    <ThemeProviderContext.Provider {...props} value={contextValue}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
